import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Optional,
  Self,
} from '@angular/core';
import { NgModel } from '@angular/forms';

type DigitalFormatMode = 'integer' | 'negativeInteger';
type DigitalFormatConfig =
  | DigitalFormatMode
  | {
      allowNegative?: boolean;
    }
  | '';

type FormatRule = 'positiveOnly' | 'allowNegative' | 'negativeOnly';

@Directive({
  selector: '[appDigitalFormat]',
})
export class DigitalFormatDirective {
  private readonly nonDigitRegExp = /[^\d]/g;
  @Input('appDigitalFormat') format?: DigitalFormatConfig;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Optional() @Self() public ngModel?: NgModel,
  ) {}

  @HostListener('blur') onBlur() {
    this.applyFormat();
  }

  @HostListener('input') onInput() {
    this.applyFormat(true);
  }

  private applyFormat(syncModel = false) {
    const rawValue = this.el.nativeElement.value || '';
    const sanitizedValue = this.sanitizeValue(rawValue);

    if (sanitizedValue !== rawValue) {
      this.el.nativeElement.value = sanitizedValue;
    }

    if (
      syncModel &&
      this.ngModel?.control &&
      this.ngModel.control.value !== sanitizedValue
    ) {
      this.ngModel.control.setValue(sanitizedValue, { emitEvent: false });
    }
  }

  private sanitizeValue(rawValue: string): string {
    const digits = rawValue.replace(this.nonDigitRegExp, '');
    const formatRule = this.resolveFormatRule();

    if (formatRule === 'positiveOnly') {
      return digits;
    }

    if (formatRule === 'negativeOnly') {
      if (!digits) {
        return rawValue.includes('-') ? '-' : '';
      }

      return `-${digits}`;
    }

    const hasLeadingMinus = rawValue.startsWith('-');
    if (!hasLeadingMinus) {
      return digits;
    }

    return digits ? `-${digits}` : '-';
  }

  private resolveFormatRule(): FormatRule {
    if (typeof this.format === 'string') {
      const mode = this.format.toLowerCase();
      if (mode === 'negativeinteger') {
        return 'negativeOnly';
      }

      return 'positiveOnly';
    }

    if (this.format && typeof this.format === 'object') {
      return this.format.allowNegative ? 'allowNegative' : 'positiveOnly';
    }

    return 'positiveOnly';
  }
}
